import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Host,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { PrimeTemplate, SelectItem } from 'primeng/api';
import { Table, TableService } from 'primeng/table';
import { ColumnInfo } from './../../../../model/src/lib/column-info.model';
import { isNullOrEmpty } from './../../../../util/src/lib/is-null-or-empty';
import { StorageService } from './../../../core/services/storage.service';
import { BaseComponent } from './../base.component';

export function tableFactory(dataTable: DataTableComponent): Table {
  return dataTable.table;
}

@Component({
  selector: 'sigv-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  providers: [
    TableService,
    { provide: Table, useFactory: tableFactory, deps: [DataTableComponent] },
  ],
})
export class DataTableComponent
  extends BaseComponent
  implements OnChanges, OnInit, AfterViewInit{
  private totalRecordsCount?: number;
  /**
   *
   * An array of objects to represent dynamic columns.<br />
   * * Required
   *
   */
  @Input() columns: ColumnInfo[] = [];
  /**
   *
   * Row data value of the component.
   * * Required
   *
   */
  @Input() value: any[] = [];
  /**
   *
   * Index of the first row to be displayed.
   *
   */
  @Input() first = 0;
  /**
   *
   * Enable data row to be selectable.<br />
   *
   */
  @Input() selectable = false;
  /**
   *
   * Enable data row to be edited.
   * * dataKey is required.
   *
   */
  @Input() editable = false;
  /**
   *
   * A property to uniquely identify a record in data.
   *
   */
  @Input() dataKey = '';
  /**
   *
   * Selected row in single mode or an array of values in multiple mode.
   *
   */
  @Input() selection: any = [];
  /**
   *
   * When specified as true, enables the pagination.
   *
   */
  @Input() paginator = false;
  /**
   *
   * Add create button below the table.
   *
   */
  @Input() canCreateRow = false;
  /**
   *
   * Enable data row to be deleted.<br />
   * * selectable must be true.
   * * dataKey is required.
   *
   */
  @Input() canDelete = false;
  /**
   *
   * 進入data row detail的Key，可依循key返回table
   */
  @Input() preferenceKey = '';
  /**
   *
   * Defines if data is loaded and interacted with in lazy manner.
   *
   */
  @Input() lazy = false;
  /**
   *
   * Displays a loader to indicate data load is in progress.
   *
   */
  @Input() loading = false;
  /**
   *
   * When specifies, enables horizontal scrolling.
   *
   */
  @Input() scrollable = false;
  /**
   *
   * Height of the scroll viewport in fixed pixels or the "flex" keyword for a dynamic size.
   *
   */
  @Input() scrollHeight = 'auto';
  /**
   *
   * Function to create data model while click create button.
   *
   */
  @Input() createRowFunc?: () => any;
  /**
   *
   * Callback to invoke when selection is changed.
   *
   */
  @Output() selectionChange = new EventEmitter<any>();
  /**
   *
   * Callback to invoke when a column gets sorted.
   *
   */
  @Output() tableSort = new EventEmitter<any>();
  /**
   *
   * 元件基本參數，無須設定
   *
   */
  @Output() tablePaging = new EventEmitter<any>();
  /**
   *
   * 元件基本參數，無須設定
   *
   */
  @Output() valueChange = new EventEmitter<any>();
  @Output() firstChange = new EventEmitter<any>();
  @ContentChildren(PrimeTemplate) templates!: QueryList<PrimeTemplate>;
  @ViewChild(Table) table!: Table;
  toolbarTemplate!: TemplateRef<any>;
  captionTemplate!: TemplateRef<any>;
  bodyTemplate!: TemplateRef<any>;
  headerTemplate!: TemplateRef<any>;
  paginatorrightTemplate!: TemplateRef<any>;
  createDialogContentTemplate!: TemplateRef<any>;
  isOpenColumnFilter = false;
  filterColumnOptions: SelectItem[] = [];
  selectedFields: string[] = [];
  noFilterColumns: ColumnInfo[] = [];
  canFilterColumns: ColumnInfo[] = [];
  displayColumns: ColumnInfo[] = [];
  clonedRows: { [s: string]: any } = {};
  showCreateDialog = false;
  rowsPerPageItems: any[] = [{ showAll: 'All' }, 10, 25, 50];

  constructor(
    @Host() private el: ElementRef,
    protected injector: Injector,
    private cd: ChangeDetectorRef,
    private storageService: StorageService
  ) {
    super(injector);
  }

  getEle(): any {
    return this.el;
  }

  get wrapperHeight(): any {
    if (this.scrollHeight === 'flex') {
      return this.el.nativeElement.parentElement.offsetHeight + 'px';
    }
    return this.scrollHeight;
  }

  get showDelete(): boolean {
    return this.selectable && this.canDelete && this.selection.length > 0;
  }

  get sort(): boolean {
    return this.columns.filter((x) => x.sortable).length > 0;
  }

  get complex(): boolean {
    return this.columns.length > 8;
  }

  get paging(): boolean {
    return this.paginator && this.value.length > 0;
  }

  get canSavePreference(): boolean {
    return isNullOrEmpty(this.preferenceKey) === false;
  }

  get totalRecords(): number {
    return this.totalRecordsCount ?? this.value.length;
  }

  @Input() set totalRecords(value: number) {
    this.totalRecordsCount = value;
  }

  get colSpanCount(): number {
    return this.displayColumns.length + (this.selectable ? 1 : 0);
  }

  get hasUnConfirmedNewRows(): boolean {
    return this.value.filter((x) => x.create).length > 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // if (changes.value && changes.value.currentValue) {
    //   this.rowsPerPageItems = [{ showAll: 'All' }, 10, 25, 50];
    // }
  }

  ngOnInit(): void {
    // 修改可支援 noFilter
    this.noFilterColumns = this.columns.filter((x) => x.noFilter);
    this.canFilterColumns = this.columns.filter((x) => !x.noFilter);
    this.filterColumnOptions = [
      { label: 'All', value: 'all' },
      ...this.canFilterColumns.map(
        (x) => ({ label: x.header, value: x.field } as SelectItem)
      ),
    ];
    this.selectedFields = [
      ...this.filterColumnOptions
        .map((x) => x.value)
        .filter(
          (x) => !this.canFilterColumns.find((c) => c.field === x)?.hidden
        ),
    ];
    if (this.filterColumnOptions.length !== this.selectedFields.length) {
      // removre seletion 'all'
      this.selectedFields.shift();
    }
    this.displayColumns = [
      ...this.noFilterColumns,
      ...this.columns.filter((x) => this.selectedFields.includes(x.field!)),
    ];
  }

  ngAfterViewInit(): void {
    if (this.canSavePreference) {
      this.table.stateStorage = 'local';
      this.table.stateKey = this.preferenceKey;
      this.table.restoreState();
    }
    this.templates.forEach((item) => {
      switch (item.getType()) {
        case 'toolbar':
          this.toolbarTemplate = item.template;
          break;
        case 'caption':
          this.captionTemplate = item.template;
          break;
        case 'header':
          this.headerTemplate = item.template;
          break;
        case 'body':
          this.bodyTemplate = item.template;
          break;
        case 'createDialogContent':
          this.createDialogContentTemplate = item.template;
          break;
        case 'paginatorright':
          this.paginatorrightTemplate = item.template;
          break;
        default:
          break;
      }
    });
    this.cd.detectChanges();
  }

  onColumnFilterBtnClick(event: any): void {
    this.isOpenColumnFilter = !this.isOpenColumnFilter;
    event.stopPropagation();
  }

  onMultiSelectClose(event: any): void {
    this.isOpenColumnFilter = false;
  }

  onMultiSelectChange(event: any): void {
    const value = event.itemValue;
    if (this.selectedFields.some((item) => item === 'all')) {
      if (value === 'all') {
        this.selectedFields = this.filterColumnOptions.map((x) => x.value);
      } else {
        if (this.selectedFields.length < this.filterColumnOptions.length) {
          this.selectedFields = this.selectedFields.filter(
            (item) => item !== 'all'
          );
        }
      }
    } else {
      if (value === 'all') {
        this.selectedFields = [];
      } else {
        if (
          this.selectedFields.length ===
          this.filterColumnOptions.length - 1
        ) {
          this.selectedFields = ['all', ...this.selectedFields];
        }
      }
    }

    this.displayColumns = this.selectedFields.reduce(
      (sum: ColumnInfo[], item: string) => {
        // 修改可支援 noFilter
        const target = this.canFilterColumns.find((x) => x.field === item);
        if (isNullOrEmpty(target) === false) {
          sum.push(target!);
        }
        return sum;
      },
      [...this.noFilterColumns]
    );
  }

  onSelectionChange(event: any): void {
    this.selectionChange.emit(event);
  }

  onTableSort(event: any): void {
    this.tableSort.emit(event);
  }

  onTablePaging(event: any): void {
    this.tablePaging.emit(event);
  }

  onFirstChange(event: any): void {
    this.firstChange.emit(event);
  }

  onRowEditInit(row: any): void {
    this.clonedRows[row[this.dataKey]] = { ...row };
  }

  onRowEditSave(row: any): void {
    delete this.clonedRows[row[this.dataKey]];
    this.valueChange.emit(this.value);
  }

  onRowEditCancel(row: any, index: number): void {
    this.value[index] = this.clonedRows[row[this.dataKey]];
    delete this.clonedRows[row[this.dataKey]];
    this.valueChange.emit(this.value);
    this.logger.debug('onRowEditCancel rows: ', this.value);
  }

  onRowCreateSave(row: any): void {
    delete row.create;
    this.valueChange.emit(this.value);
    this.logger.debug('onRowCreateSave rows: ', this.value);
  }

  onRowCreateCancel(row: any, index: number): void {
    this.value.splice(index);
    // this.valueChange.emit(this.value);
    // this.logger.debug('onRowCreateCancel rows: ', this.value);
  }

  onCreateRowBtnClick(): void {
    if (this.complex) {
      this.showCreateDialog = true;
      return;
    }
    this.addNewRow();
  }

  onCreateRowDialogConfirm(): void {
    this.addNewRow();
    this.showCreateDialog = false;
  }

  onTableStateSave(event: any): void {
    if (this.canSavePreference) {
      const state = this.storageService.local.getItem(this.preferenceKey);
      const preference = {
        columnOrder: state.columnOrder,
        first: 0,
        rows: state.rows,
      };
      this.storageService.local.setItem(this.preferenceKey, {
        ...event,
        ...preference,
      });
    }
  }

  onTableStateRestore(event: any): void {
    if (this.canSavePreference) {
      const columnOrder = event.columnOrder;
      const allColumnOptions = this.canFilterColumns.map((x) => ({
        label: x.header,
        value: x.field,
      })) as SelectItem[];
      this.selectedFields = [...columnOrder];
      this.displayColumns = this.selectedFields.map(
        (x) => this.columns.find((y) => y.field === x)!
      );

      const unCheckedOptions = [] as SelectItem[];
      const checkedOptions = [] as SelectItem[];
      allColumnOptions.forEach((x) => {
        if (columnOrder.includes(x.value) === false) {
          unCheckedOptions.push(x);
        } else {
          checkedOptions.push(x);
        }
      });

      this.filterColumnOptions = [
        { label: 'All', value: 'all' },
        ...checkedOptions,
        ...unCheckedOptions,
      ];
    }
  }

  onColumnOptionMultiSelectPanelHide(): void {
    if (this.canSavePreference) {
      this.table.saveState();
    }
  }

  onColReorder(event: any): void {
    if (this.canSavePreference) {
      const allColumnOptions = this.canFilterColumns.map((x) => ({
        label: x.header,
        value: x.field,
      })) as SelectItem[];
      const unCheckedOptions = [] as SelectItem[];
      const checkedOptions = [] as SelectItem[];
      allColumnOptions.forEach((x) => {
        if (!this.displayColumns.map((y) => y.field).includes(x.value)) {
          unCheckedOptions.push(x);
        } else {
          checkedOptions.push(x);
        }
      });

      this.filterColumnOptions = [
        { label: 'All', value: 'all' },
        ...checkedOptions,
        ...unCheckedOptions,
      ];
    }
  }

  onDeleteBtnClick(): void {
    this.value = this.value.filter((x) => !this.selection.includes(x));
    this.selection = [];
    this.selectionChange.emit(this.selection);
    this.valueChange.emit(this.value);
    this.logger.debug('onDeleteBtnClick rows: ', this.value);
  }

  private addNewRow(): void {
    if (isNullOrEmpty(this.createRowFunc)) {
      throw new Error('createRowFunc is null.');
    }
    const newRow = this.createRowFunc!();
    if (isNullOrEmpty(newRow)) {
      return;
    }
    if (this.complex) {
      this.value = [...this.value, newRow];
      this.valueChange.emit(this.value);
      this.logger.debug('addNewRow rows: ', this.value);
      return;
    }
    newRow.create = true;
    this.value = [...this.value, newRow];
  }
}
